import { useState } from 'react';
import { toast } from 'sonner';
import { FileDown, Loader2 } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import api from '../lib/api';
import { saveBlob } from '../lib/saveBlob';
import { useOnlineStatus } from '../lib/useOnlineStatus';
import { OnlineOnlyNotice } from '../components/OnlineOnlyNotice';

const isoDay = (d) => d.toISOString().slice(0, 10);

export const downloadFdaForms = async (body) => {
  if (!navigator.onLine) {
    toast.error('Exporting FDA forms needs internet', { description: 'Reconnect and try again.' });
    return false;
  }
  try {
    const res = await api.post('/staff/portal/adverse-reactions/export', body, { responseType: 'blob' });
    const name = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'ghana-fda-adr.pdf';
    saveBlob(res.data, name);
    const left = Number(res.headers['x-reports-remaining'] || 0);
    toast.success(`${res.headers['x-report-count'] || ''} FDA report form(s) downloaded${left ? ` — ${left} more remain, export again for the next batch` : ''}`);
    return true;
  } catch (e) {
    let msg = 'Export failed';
    try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* non-JSON */ }
    toast.error(msg);
    return false;
  }
};

export const FdaExportDialog = ({ open, onOpenChange, onDone }) => {
  const [from, setFrom] = useState(isoDay(new Date(Date.now() - 30 * 864e5)));
  const [to, setTo] = useState(isoDay(new Date()));
  const [onlyNew, setOnlyNew] = useState(true);
  const [identity, setIdentity] = useState('initials');
  const [busy, setBusy] = useState(false);
  const online = useOnlineStatus();

  const run = async () => {
    setBusy(true);
    const ok = await downloadFdaForms({ date_from: from, date_to: to, only_unexported: onlyNew, identity });
    setBusy(false);
    if (ok) { onOpenChange(false); onDone?.(); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fda-export-dialog">
        <DialogHeader>
          <DialogTitle>Export for Ghana FDA</DialogTitle>
          <DialogDescription>One filled-in ADR reporting form per reaction, ready to print, sign and send.</DialogDescription>
        </DialogHeader>
        <OnlineOnlyNotice feature="Exporting FDA forms" testId="fda-export-offline-notice" />
        <div className="grid grid-cols-2 gap-3">
          <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="fda-export-from" /></div>
          <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="fda-export-to" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={onlyNew} onCheckedChange={(v) => setOnlyNew(!!v)} data-testid="fda-export-only-new" />
          Only reports not exported yet
        </label>
        <div>
          <Label>Patient details on the form</Label>
          <RadioGroup value={identity} onValueChange={setIdentity} className="mt-2 space-y-1">
            <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="initials" data-testid="fda-export-identity-initials" /> Initials, age and sex (FDA standard)</label>
            <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="full" data-testid="fda-export-identity-full" /> Full name and phone number</label>
          </RadioGroup>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="fda-export-cancel">Cancel</Button>
          <Button onClick={run} disabled={busy || !online} data-testid="fda-export-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />} Download forms
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
